/* biome-ignore-all lint/suspicious/noExplicitAny: form filter values */
import { useEffect } from "react"
import { Button, Card, Col, Form, Input, Row, Select, Space, Tag, Tooltip } from "antd"
import { SearchOutlined, ReloadOutlined, DownloadOutlined, FileTextOutlined, SyncOutlined } from "@ant-design/icons"
import type React from "react"
import type { SystemLogFileItem, SystemLogQueryParams } from "@/services/log/systemLogApi"
import dayjs from "dayjs"

interface SystemLogFilterFormProps {
  files: SystemLogFileItem[]
  currentFile: string
  onSearch: (values: SystemLogQueryParams) => void
  onDownload: () => void
  loading?: boolean
  initialValues?: SystemLogQueryParams
  autoRefreshInterval: number
  onAutoRefreshChange: (seconds: number) => void
}

export const SystemLogFilterForm: React.FC<SystemLogFilterFormProps> = ({
  files,
  currentFile,
  onSearch,
  onDownload,
  loading = false,
  initialValues,
  autoRefreshInterval,
  onAutoRefreshChange,
}) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (currentFile) {
      form.setFieldValue("file", currentFile)
    }
  }, [currentFile, form])

  const handleFinish = (values: any) => {
    onSearch(values)
  }

  const handleReset = () => {
    form.resetFields()
    form.setFieldValue("file", currentFile)
    onSearch({ file: currentFile })
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "0 B"
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const fileOptions = files.map((f) => ({
    label: (
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-mono text-gray-800 text-xs">
          <FileTextOutlined className="text-gray-400" />
          {f.name}
          {f.is_current && (
            <Tag color="blue" className="m-0 px-1 py-0 text-[10px] leading-3">
              current
            </Tag>
          )}
        </span>
        <span className="font-mono text-[11px] text-gray-400">
          {formatFileSize(f.size)} • {dayjs(f.mtime).format("DD/MM HH:mm")}
        </span>
      </div>
    ),
    value: f.name,
  }))

  return (
    <Card className="mb-2 shadow-xs">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          file: initialValues?.file || currentFile,
          level: initialValues?.level || "all",
          status_code: initialValues?.status_code || "all",
          search: initialValues?.search || "",
        }}
      >
        <Row gutter={[16, 8]}>
          {/* 1. Chọn file log */}
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item label="File nhật ký (Log file)" name="file">
              <Select
                placeholder="Chọn file log..."
                options={fileOptions}
                showSearch
                optionFilterProp="value"
                className="w-full"
              />
            </Form.Item>
          </Col>

          {/* 2. Cấp độ log (Level) */}
          <Col xs={24} sm={12} md={5} lg={4}>
            <Form.Item label="Cấp độ log" name="level">
              <Select
                options={[
                  { label: "Tất cả cấp độ", value: "all" },
                  {
                    label: (
                      <span>
                        <Tag color="error" className="m-0 mr-1 px-1 py-0 text-[11px]">
                          ERROR
                        </Tag>{" "}
                        Lỗi (≥ 50)
                      </span>
                    ),
                    value: "error",
                  },
                  {
                    label: (
                      <span>
                        <Tag color="warning" className="m-0 mr-1 px-1 py-0 text-[11px]">
                          WARN
                        </Tag>{" "}
                        Cảnh báo (40)
                      </span>
                    ),
                    value: "warn",
                  },
                  {
                    label: (
                      <span>
                        <Tag color="processing" className="m-0 mr-1 px-1 py-0 text-[11px]">
                          INFO
                        </Tag>{" "}
                        Thông tin (30)
                      </span>
                    ),
                    value: "info",
                  },
                  {
                    label: (
                      <span>
                        <Tag color="purple" className="m-0 mr-1 px-1 py-0 text-[11px]">
                          DEBUG
                        </Tag>{" "}
                        Debug (≤ 20)
                      </span>
                    ),
                    value: "debug",
                  },
                ]}
              />
            </Form.Item>
          </Col>

          {/* 3. HTTP Status Code */}
          <Col xs={24} sm={12} md={5} lg={4}>
            <Form.Item label="Mã lỗi HTTP" name="status_code">
              <Select
                options={[
                  { label: "Tất cả mã", value: "all" },
                  { label: "5xx (Lỗi Server)", value: "5xx" },
                  { label: "4xx (Lỗi Client)", value: "4xx" },
                  { label: "500 Internal Error", value: "500" },
                  { label: "404 Not Found", value: "404" },
                  { label: "400 Bad Request", value: "400" },
                  { label: "401 / 403 Auth Error", value: "401" },
                ]}
              />
            </Form.Item>
          </Col>

          {/* 4. Tìm kiếm từ khóa */}
          <Col xs={24} sm={12} md={6} lg={9}>
            <Form.Item label="Tìm kiếm từ khóa" name="search">
              <Input placeholder="Nhập thông điệp lỗi, route, stack trace, hoặc từ khóa..." allowClear />
            </Form.Item>
          </Col>

          {/* Hàng nút thao tác & Auto-refresh */}
          <Col xs={24} className="flex flex-wrap items-center justify-between gap-2 border-gray-100 border-t pt-1">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 font-medium text-gray-500 text-xs">
                <SyncOutlined
                  spin={autoRefreshInterval > 0}
                  className={autoRefreshInterval > 0 ? "text-blue-500" : "text-gray-400"}
                />
                Tự động làm mới:
              </span>
              <Select
                size="small"
                value={autoRefreshInterval}
                onChange={onAutoRefreshChange}
                className="w-28"
                options={[
                  { label: "Tắt", value: 0 },
                  { label: "Mỗi 5 giây", value: 5 },
                  { label: "Mỗi 10 giây", value: 10 },
                  { label: "Mỗi 30 giây", value: 30 },
                ]}
              />
            </div>

            <Space>
              <Tooltip title="Tải file log này về máy để đọc offline">
                <Button icon={<DownloadOutlined />} onClick={onDownload}>
                  Tải file log
                </Button>
              </Tooltip>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Đặt lại
              </Button>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}

export default SystemLogFilterForm
